import axios  from 'axios';
import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { refreshAccessToken as refreshAccessTokenService } from '../Authentication/TokenService';
import {jwtDecode} from 'jwt-decode';



interface AuthContextProps {
  isAuthenticated: boolean;
  login: (email: string, password: string, navigate: (path: string) => void) => Promise<void>;
  logout: () => void;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  enable2Fa: () => Promise<void>;
  disable2Fa: () => Promise<void>;
  accessToken?: string | undefined;
  refreshToken?: string | undefined;
  user?: CustomJwtPayload;
  setAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  setAuthState: (accessToken: string, refreshToken: string, email: string) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface CustomJwtPayload {
  exp: number;
  roles?: string[];
  twoFactorEnabled?: boolean;
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"?: string;
  [key: string]: any;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | undefined>(localStorage.getItem('accessToken') || undefined);
  const [refreshToken, setRefreshToken] = useState<string | undefined>(localStorage.getItem('refreshToken') || undefined);
  const [user, setUser] = useState<CustomJwtPayload | undefined>(undefined);


  const apiUrl = process.env.REACT_APP_API_URL;
  console.log('API URL:', apiUrl);

  const isTokenExpired = (token: string) => {
    if (!token) return true; // Token is empty or undefined
    try {
      const decodedToken = jwtDecode<CustomJwtPayload>(token);
      return decodedToken.exp * 1000 < Date.now();
    } catch (e) {
      console.error('Failed to decode token', e);
      return true; // Treat as expired if decoding fails
    }
  };

  const decodeToken = (token: string) => {
    try {
      const decodedToken = jwtDecode<CustomJwtPayload>(token);
      setUser(decodedToken);
    } catch (e) {
      console.error('Failed to decode token', e);
      setUser(undefined);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      if (accessToken && refreshToken) {
        if (isTokenExpired(accessToken)) {
          const tokens = await refreshAccessTokenService();
          if (tokens) {
            setAccessToken(tokens.accessToken);
            setRefreshToken(tokens.refreshToken);
            localStorage.setItem('accessToken', tokens.accessToken);
            localStorage.setItem('refreshToken', tokens.refreshToken);
            decodeToken(tokens.accessToken);
          }
        } else {
          if (accessToken) {
            if (accessToken) {
                if (accessToken) {
                    if (accessToken) {
                        if (accessToken) {
                            decodeToken(accessToken);
                        }
                    }
                }
            }
          }
        }
        setIsAuthenticated(true);
        console.log('User is authenticated:', true);
      } else {
        setIsAuthenticated(false);
        console.log('User is authenticated:', false);
      }
    };

    initializeAuth();

    const interval = setInterval(async () => {
      if (accessToken && isTokenExpired(accessToken)) {
        const tokens = await refreshAccessTokenService();
        if (tokens) {
          setAccessToken(tokens.accessToken);
          setRefreshToken(tokens.refreshToken);
          localStorage.setItem('accessToken', tokens.accessToken);
          localStorage.setItem('refreshToken', tokens.refreshToken);
          decodeToken(tokens.accessToken);
        } else {
          setIsAuthenticated(false);
          setUser(undefined);
        }
      }
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [accessToken, refreshToken]);

  const login = async (email: string, password: string, navigate: (path: string) => void) => {
    try {
        console.log('API URL:', apiUrl);
        console.log('Login request payload:', { email, password });

        const response = await axios.post<{ requires2FA: boolean; userId?: string; accessToken?: string; refreshToken?: string }>(`${apiUrl}/Auth/login`, { email, password });

        console.log('Login response:', response);

        if (response.data.requires2FA) {
            navigate(`/verify-2fa?userId=${response.data.userId}`);
        } else if (response.status === 200) {
            const { accessToken, refreshToken } = response.data;
            if (accessToken) {
                localStorage.setItem('accessToken', accessToken);
            }
            if (refreshToken) {
                localStorage.setItem('refreshToken', refreshToken);
            }
            localStorage.setItem('email', email);
            setAccessToken(accessToken);
            setRefreshToken(refreshToken);
            decodeToken(accessToken || '');
            setIsAuthenticated(true);
            console.log('Login successful, user is authenticated:', true);
        }
    } catch (error: any) {
        console.error("Login failed", error);
        if ((error) && error.response) {
            throw new Error(error.response.data.message || "Invalid email or password. Please try again.");
        } else {
            throw new Error("An unexpected error occurred. Please try again.");
        }
    }
};

  const setAuthState = (accessToken: string, refreshToken: string) => {
    const decodedToken = jwtDecode(accessToken);
    const email = (decodedToken as CustomJwtPayload)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('email', email ?? '');
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    decodeToken(accessToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setAccessToken(undefined);
    setRefreshToken(undefined);
    setUser(undefined);
    console.log('User logged out, isAuthenticated:', false);
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await axios.post(
        `${apiUrl}/Admin/changePassword`,
        { currentPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      alert('Password changed successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password.');
    }
  };

  const enable2Fa = async () => {
    try {
      await axios.post(
        `${apiUrl}/Admin/enable2Fa`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      setUser((prevUser) => ({ ...prevUser, twoFactorEnabled: true, exp: prevUser?.exp || 0, roles: prevUser?.roles || [] }));
      alert('Two Factor Authentication enabled successfully!');
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      alert('Failed to enable Two Factor Authentication.');
    }
  };

  const disable2Fa = async () => {
    try {
      await axios.post(
        `${apiUrl}/Admin/disable2Fa`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      setUser((prevUser) => prevUser ? { ...prevUser, twoFactorEnabled: false } : undefined);
      alert('Two Factor Authentication disabled successfully!');
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      alert('Failed to disable Two Factor Authentication.');
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, updatePassword, enable2Fa, disable2Fa, accessToken, refreshToken, user, setAuthState, setAccessToken, setRefreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};